"use client"

import type { ReactNode } from "react"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

import { getFirebaseAuth, getGoogleProvider } from "@/lib/firebase-client"

// A sign-in that Google accepted but the site could not turn into a session.
// `reference` names the failing step, so it can be shown on its own line.
export class SessionError extends Error {
  reference?: string

  constructor(message: string, reference?: string) {
    super(message)
    this.reference = reference
  }
}

// Plain-language reasons for the Google popup failing, keyed by Firebase code.
const POPUP_ERRORS: Record<string, string> = {
  "auth/popup-blocked": "Your browser blocked the Google window. Allow pop-ups for this site and try again.",
  "auth/popup-closed-by-user": "The Google window closed before sign-in finished. Please try again.",
  "auth/cancelled-popup-request": "The Google window closed before sign-in finished. Please try again.",
  "auth/user-cancelled": "Sign-in was cancelled. Please try again.",
  "auth/unauthorized-domain": "Google sign-in isn't enabled for this web address yet.",
  "auth/operation-not-allowed": "Google sign-in isn't enabled for this site yet.",
  "auth/network-request-failed": "Couldn't reach Google. Check your connection and try again.",
  "auth/too-many-requests": "Too many attempts. Wait a minute and try again.",
  "auth/web-storage-unsupported": "Your browser is blocking the storage sign-in needs. Allow cookies for this site and try again.",
}

function popupErrorMessage(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code
  return (typeof code === "string" && POPUP_ERRORS[code]) || "Sign-in didn't finish. Please try again."
}

export interface AuthUser {
  id: string
  name: string | null
  email: string | null
  photoURL: string | null
  isAdmin: boolean
  isEditor: boolean
  isSubscriber: boolean
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  isAdmin: boolean
  isEditor: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  // The popup result and the auth-state listener both fire on one sign-in;
  // they share a single session request instead of racing two.
  const pending = useRef<{ uid: string; promise: Promise<AuthUser> } | null>(null)

  // Exchange a Firebase ID token for a ModulifyDB-backed session cookie.
  // Throws when the session could not be started, so callers never treat a
  // failed sign-in as a successful one.
  const establish = useCallback((fbUser: any): Promise<AuthUser> => {
    if (pending.current?.uid === fbUser.uid) return pending.current.promise

    const promise = (async () => {
      const token = await fbUser.getIdToken()
      const res = await fetch("/api/auth/session", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          // Lets the server keep the session inside the editor preview frame.
          "X-Embedded": window.self !== window.top ? "1" : "0",
        },
        // Also in the body, since a proxy can strip the Authorization header.
        body: JSON.stringify({ idToken: token }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.user) {
        setUser(null)
        if (data.code === "not-configured") {
          throw new SessionError("Sign-in isn't set up on the server yet.")
        }
        // A short reference, so a failure can be traced without guessing.
        // The detail goes first, since it is the part that names the cause.
        const reference = [data.detail, data.reason, data.code || `http-${res.status}`].filter(Boolean).join(" / ")
        throw new SessionError(
          "Google confirmed your account, but your session could not be started. Please try again.",
          reference
        )
      }
      setUser(data.user)
      return data.user as AuthUser
    })()

    pending.current = { uid: fbUser.uid, promise }
    promise.then(
      () => { if (pending.current?.promise === promise) pending.current = null },
      () => { if (pending.current?.promise === promise) pending.current = null }
    )
    return promise
  }, [])

  useEffect(() => {
    let unsubscribe = () => {}
    let active = true

    ;(async () => {
      const auth = getFirebaseAuth()
      if (!auth) {
        // Firebase web config not provided yet; treat as signed out.
        if (active) setLoading(false)
        return
      }
      const { onAuthStateChanged } = await import("firebase/auth")
      unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
        if (fbUser) {
          try {
            await establish(fbUser)
          } catch {
            setUser(null)
          }
        } else {
          setUser(null)
        }
        if (active) setLoading(false)
      })
    })()

    return () => {
      active = false
      unsubscribe()
    }
  }, [establish])

  const signInWithGoogle = useCallback(async () => {
    const auth = getFirebaseAuth()
    if (!auth) {
      throw new Error("Sign-in isn't set up yet.")
    }
    const { signInWithPopup, signOut: firebaseSignOut } = await import("firebase/auth")

    let result
    try {
      result = await signInWithPopup(auth, getGoogleProvider())
    } catch (error) {
      // A warning, not an error: the sign-in page already shows the reason,
      // and an error here would cover the page in development.
      console.warn("Google sign-in failed:", (error as { code?: unknown } | null)?.code ?? error)
      throw new Error(popupErrorMessage(error))
    }

    try {
      await establish(result.user)
    } catch (error) {
      console.warn("Session could not be started:", error instanceof Error ? error.message : error)
      // Keep Google and the site in step, so the next try starts clean.
      await firebaseSignOut(auth).catch(() => {})
      throw error instanceof SessionError
        ? error
        : new Error("Google confirmed your account, but your session could not be started. Please try again.")
    }
  }, [establish])

  const signOut = useCallback(async () => {
    const auth = getFirebaseAuth()
    try {
      if (auth) {
        const { signOut: firebaseSignOut } = await import("firebase/auth")
        await firebaseSignOut(auth)
      }
    } catch {
      // ignore
    }
    try {
      await fetch("/api/auth/logout", { method: "POST" })
    } catch {
      // ignore
    }
    setUser(null)
    if (typeof window !== "undefined") {
      window.location.href = "/"
    }
  }, [])

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me", { cache: "no-store" })
      if (res.ok) {
        const data = await res.json()
        setUser(data.user ?? null)
      }
    } catch {
      // ignore
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin: !!user?.isAdmin,
        isEditor: !!user?.isEditor,
        signInWithGoogle,
        signOut,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return ctx
}
